import type { MemoizedFn } from 'memoize-one';
import memoizeOne from 'memoize-one';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { expVal } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import CollabPermissionTokenQuery, {
	type CollabPermissionTokenQuery$data,
	type CollabPermissionTokenQuery$variables,
} from './__generated__/CollabPermissionTokenQuery.graphql.ts';
import { nativeCollabPermissionsTokenState } from './CollabPermissionsTokenState.ts';

const isContentNotFoundError = (error): boolean => {
	if (error && 'graphQLErrors' in error) {
		return error.graphQLErrors?.some((graphQLError) =>
			graphQLError.message.includes('Invalid content id'),
		);
	}
	return false;
};

export const fetchCollabPermissionToken: MemoizedFn<
	(contentId: string, draftShareId: string | undefined) => () => Promise<string | null>
> = memoizeOne(
	(contentId: string, draftShareId: string | undefined): (() => Promise<string | null>) =>
		async (): Promise<string | null> => {
			const permissionsTokenState =
				nativeCollabPermissionsTokenState.getPermissionsTokenState(contentId);
			if (permissionsTokenState) {
				const { getToken, isUsedForDraftFetch, isUsedForWSInitialization } = permissionsTokenState;
				// fetchCollabPermissionToken should only return the token if it is valid and has successfully been passed to the getDraft call
				if (isUsedForDraftFetch && !isUsedForWSInitialization) {
					const token = await getToken;
					if (token) {
						nativeCollabPermissionsTokenState.setIsUsedForWSInitialization(contentId, true);
						return token;
					}
				}
			}
			try {
				CollabEditProviderMeasures.markMeasureStart(
					COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_PERMS_TOKEN_FETCH,
				);
				const result = await queryInterop<
					CollabPermissionTokenQuery$data,
					CollabPermissionTokenQuery$variables
				>({
					query: CollabPermissionTokenQuery,
					variables: {
						contentId,
						...(draftShareId && { draftShareId }),
					},
					fetchPolicy: 'network-only',
					context: {
						fetchOptions: {
							// If we have a "no network error" this will never resolve and subsequent calls will
							// be in limbo. This means we are unable to ever connect to NCS again (requiring a page reset)
							ignoreNoNetworkError: true,
						},
					},
				});
				CollabEditProviderMeasures.markMeasureEnd(
					COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_PERMS_TOKEN_FETCH,
				);
				const token = result.data?.collabToken?.token ?? null; // if token is null, collab service will fall back to permissions service check
				if (token === null && expVal('platform_editor_emit_null_token_event', 'isEnabled', false)) {
					void getAnalyticsWebClient().then((client) => {
						client.sendOperationalEvent({
							action: 'nullTokenReturned',
							actionSubject: 'collabPermissionToken',
							source: 'fetchCollabPermissionToken',
							attributes: {
								reason: 'emptyResponse',
							},
						});
					});
				}
				return token;
			} catch (e) {
				markErrorAsHandled(e);
				if (isContentNotFoundError(e)) {
					// nasty fragile hack, mimic 404 exception which goes through collab-provider, then gets wrapped and emitted and handled in `useError`.
					// collab provider simply emits errors thrown in permissionTokenRefresh directly on its internal channel, so we have to conform to their error type
					// prettier-ignore
					throw { message: "Content not found", data: { status: 404, code: "INSUFFICIENT_EDITING_PERMISSION", meta: { description: "RESOURCE_DELETED", reason: "RESOURCE_DELETED" }}};
				} else if (e?.networkError?.response?.status === 503) {
					// prettier-ignore
					throw { message: "Service unavailable", data: { meta: { reason: "RESOURCE_DELETED" }}}; // temporary hack to ensure draft cannot be edited during tenant migration, but we should throw TENANT_INSTANCE_MAINTENANCE once supported by collab-provider
					// throw { message: "Service unavailable", data: { status: 423, code: "TENANT_INSTANCE_MAINTENANCE", meta: { description: "Service unavailable" }}};
				}
				if (expVal('platform_editor_emit_null_token_event', 'isEnabled', false)) {
					void getAnalyticsWebClient().then((client) => {
						client.sendOperationalEvent({
							action: 'nullTokenReturned',
							actionSubject: 'collabPermissionToken',
							source: 'fetchCollabPermissionToken',
							attributes: {
								reason: 'fetchError',
							},
						});
					});
				}
				return null;
			}
		},
);
